// Pen-tool primitives — anchor types, paths, and adaptive tessellation.
// Used by the 2D PenCanvas (vase profile, cross-section, vector pattern).
// Tessellation output is the same `{ z, r }` shape as ProfilePoint so existing
// mesh/shader code paths can consume it without changes (see vaseModel.effectiveProfile).

export type AnchorKind = 'line' | 'quad' | 'cubic' | 'arc';
export type AnchorContinuity = 'cusp' | 'smooth' | 'symmetric';

export interface Vec2 {
  z: number;
  r: number;
}

export interface PenAnchor {
  id: string;
  z: number;
  r: number;
  /** Segment kind from PREVIOUS anchor to THIS anchor. First anchor's kind is ignored. */
  kind: AnchorKind;
  /** quadratic: single off-curve handle. cubic: outgoing handle of PREVIOUS anchor. */
  cp1?: Vec2;
  /** cubic only: incoming handle of THIS anchor. */
  cp2?: Vec2;
  /** arc only: midpoint defining the arc through prev → arcMid → this. */
  arcMid?: Vec2;
  continuity?: AnchorContinuity;
}

export interface PenPath {
  anchors: PenAnchor[];
  closed: boolean;
}

const DEFAULT_CHORD_ERR = 0.05; // mm

/** Stable id generator (timestamp + counter, good enough for in-app use). */
let _idCounter = 0;
export function newAnchorId(): string {
  _idCounter = (_idCounter + 1) | 0;
  return `a${Date.now().toString(36)}${_idCounter.toString(36)}`;
}

export function makeAnchor(z: number, r: number, kind: AnchorKind = 'line'): PenAnchor {
  return { id: newAnchorId(), z, r, kind, continuity: 'smooth' };
}

function lerp2(a: Vec2, b: Vec2, t: number): Vec2 {
  return { z: a.z + (b.z - a.z) * t, r: a.r + (b.r - a.r) * t };
}

function dist2(a: Vec2, b: Vec2): number {
  const dz = a.z - b.z, dr = a.r - b.r;
  return Math.sqrt(dz * dz + dr * dr);
}

/** Perpendicular distance from p to line through a,b. */
function perpDist(p: Vec2, a: Vec2, b: Vec2): number {
  const dz = b.z - a.z, dr = b.r - a.r;
  const len2 = dz * dz + dr * dr;
  if (len2 < 1e-12) return dist2(p, a);
  const cross = dz * (a.r - p.r) - dr * (a.z - p.z);
  return Math.abs(cross) / Math.sqrt(len2);
}

function quadAt(p0: Vec2, c: Vec2, p1: Vec2, t: number): Vec2 {
  const u = 1 - t;
  return {
    z: u * u * p0.z + 2 * u * t * c.z + t * t * p1.z,
    r: u * u * p0.r + 2 * u * t * c.r + t * t * p1.r
  };
}

function cubicAt(p0: Vec2, c1: Vec2, c2: Vec2, p1: Vec2, t: number): Vec2 {
  const u = 1 - t;
  const uu = u * u, tt = t * t;
  return {
    z: uu * u * p0.z + 3 * uu * t * c1.z + 3 * u * tt * c2.z + tt * t * p1.z,
    r: uu * u * p0.r + 3 * uu * t * c1.r + 3 * u * tt * c2.r + tt * t * p1.r
  };
}

/** Recursive adaptive sampling for a parametric curve fn(t) over [t0,t1].
 *  Splits when chord error exceeds tolerance. Output excludes the start point
 *  (caller appends start once). */
function adaptiveSample(
  fn: (t: number) => Vec2,
  t0: number,
  t1: number,
  p0: Vec2,
  p1: Vec2,
  tol: number,
  out: Vec2[],
  depth = 0
): void {
  if (depth > 16) {
    out.push(p1);
    return;
  }
  const tm = (t0 + t1) * 0.5;
  const pm = fn(tm);
  if (perpDist(pm, p0, p1) <= tol && dist2(p0, p1) < 8) {
    out.push(p1);
    return;
  }
  adaptiveSample(fn, t0, tm, p0, pm, tol, out, depth + 1);
  adaptiveSample(fn, tm, t1, pm, p1, tol, out, depth + 1);
}

/** Fit a circle through 3 points and sample the arc from p0 to p1 going through pm.
 *  Falls back to straight line when points are collinear. */
function sampleArc(p0: Vec2, pm: Vec2, p1: Vec2, tol: number): Vec2[] {
  const az = p0.z, ar = p0.r;
  const bz = pm.z, br = pm.r;
  const cz = p1.z, cr = p1.r;
  const d = 2 * (az * (br - cr) + bz * (cr - ar) + cz * (ar - br));
  if (Math.abs(d) < 1e-9) return [p1];
  const az2 = az * az + ar * ar;
  const bz2 = bz * bz + br * br;
  const cz2 = cz * cz + cr * cr;
  const cx = (az2 * (br - cr) + bz2 * (cr - ar) + cz2 * (ar - br)) / d;
  const cy = (az2 * (cz - bz) + bz2 * (az - cz) + cz2 * (bz - az)) / d;
  const center: Vec2 = { z: cx, r: cy };
  const radius = dist2(center, p0);
  const a0 = Math.atan2(p0.r - cy, p0.z - cx);
  const am = Math.atan2(pm.r - cy, pm.z - cx);
  const a1 = Math.atan2(p1.r - cy, p1.z - cx);
  // Decide direction (CCW vs CW) so arc passes through am.
  function norm(a: number): number { return ((a % (2 * Math.PI)) + 2 * Math.PI) % (2 * Math.PI); }
  const ccwSweep = norm(a1 - a0);
  const ccwToMid = norm(am - a0);
  const ccw = ccwToMid <= ccwSweep;
  const sweep = ccw ? ccwSweep : -(2 * Math.PI - ccwSweep);
  // Number of segments based on chord error tolerance.
  const minSegs = 4;
  const maxSegs = 64;
  const dtheta = 2 * Math.acos(Math.max(0, 1 - tol / radius));
  let n = isFinite(dtheta) && dtheta > 0 ? Math.ceil(Math.abs(sweep) / dtheta) : minSegs;
  n = Math.max(minSegs, Math.min(maxSegs, n));
  const out: Vec2[] = [];
  for (let i = 1; i <= n; i++) {
    const t = i / n;
    const a = a0 + sweep * t;
    out.push({ z: cx + radius * Math.cos(a), r: cy + radius * Math.sin(a) });
  }
  return out;
}

/** Tessellate a path into a flat polyline of {z, r} points.
 *  `samples` is a hint for the minimum total sample count for the whole path
 *  — adaptive sampling will exceed it as needed for accuracy. */
export function tessellatePath(path: PenPath, samples = 64): Vec2[] {
  const { anchors, closed } = path;
  if (anchors.length === 0) return [];
  if (anchors.length === 1) return [{ z: anchors[0].z, r: anchors[0].r }];
  const tol = DEFAULT_CHORD_ERR;
  const out: Vec2[] = [{ z: anchors[0].z, r: anchors[0].r }];
  const segCount = closed ? anchors.length : anchors.length - 1;
  for (let i = 0; i < segCount; i++) {
    const prev = anchors[i];
    const cur = anchors[(i + 1) % anchors.length];
    const p0: Vec2 = { z: prev.z, r: prev.r };
    const p1: Vec2 = { z: cur.z, r: cur.r };
    switch (cur.kind) {
      case 'line': {
        out.push(p1);
        break;
      }
      case 'quad': {
        const c = cur.cp1 ?? lerp2(p0, p1, 0.5);
        adaptiveSample((t) => quadAt(p0, c, p1, t), 0, 1, p0, p1, tol, out);
        break;
      }
      case 'cubic': {
        const c1 = cur.cp1 ?? lerp2(p0, p1, 1 / 3);
        const c2 = cur.cp2 ?? lerp2(p0, p1, 2 / 3);
        adaptiveSample((t) => cubicAt(p0, c1, c2, p1, t), 0, 1, p0, p1, tol, out);
        break;
      }
      case 'arc': {
        const m = cur.arcMid ?? lerp2(p0, p1, 0.5);
        const arcPts = sampleArc(p0, m, p1, tol);
        for (const pt of arcPts) out.push(pt);
        break;
      }
    }
  }
  // Honor minimum-samples hint by linear-resampling if we're well below it.
  if (out.length < samples) {
    return resampleByCount(out, samples);
  }
  return out;
}

function resampleByCount(poly: Vec2[], n: number): Vec2[] {
  if (poly.length < 2 || n <= poly.length) return poly;
  const lens: number[] = [0];
  for (let i = 1; i < poly.length; i++) lens.push(lens[i - 1] + dist2(poly[i - 1], poly[i]));
  const total = lens[lens.length - 1];
  if (total < 1e-9) return poly;
  const out: Vec2[] = [poly[0]];
  let j = 0;
  for (let i = 1; i < n - 1; i++) {
    const target = (i / (n - 1)) * total;
    while (j < lens.length - 2 && lens[j + 1] < target) j++;
    const t = (target - lens[j]) / Math.max(1e-9, lens[j + 1] - lens[j]);
    out.push(lerp2(poly[j], poly[j + 1], t));
  }
  out.push(poly[poly.length - 1]);
  return out;
}

export function pathBoundingBox(path: PenPath): { zMin: number; zMax: number; rMin: number; rMax: number } {
  if (path.anchors.length === 0) return { zMin: 0, zMax: 0, rMin: 0, rMax: 0 };
  let zMin = Infinity, zMax = -Infinity, rMin = Infinity, rMax = -Infinity;
  for (const a of path.anchors) {
    if (a.z < zMin) zMin = a.z;
    if (a.z > zMax) zMax = a.z;
    if (a.r < rMin) rMin = a.r;
    if (a.r > rMax) rMax = a.r;
  }
  return { zMin, zMax, rMin, rMax };
}

/** Convert a flat ProfilePoint[] into a Pen path made of straight-line anchors.
 *  Used when migrating an existing legacy profile into Pen Mode. */
export function profileToPenPath(pts: { z: number; r: number }[]): PenPath {
  return {
    anchors: pts.map((p, i) => ({
      id: newAnchorId(),
      z: p.z,
      r: p.r,
      kind: i === 0 ? 'line' : 'line',
      continuity: 'smooth' as const
    })),
    closed: false
  };
}

/** Smooth a single anchor by Catmull-Rom-style averaging with its neighbors,
 *  then convert it to a cubic with auto-calculated tangent handles. Strength
 *  controls handle length as fraction of neighbor distance (0..1, typical 0.25). */
export function smoothAnchor(path: PenPath, idx: number, strength = 0.25): void {
  const a = path.anchors[idx];
  if (!a) return;
  const n = path.anchors.length;
  const closed = path.closed;
  const prev = closed ? path.anchors[(idx - 1 + n) % n] : (idx > 0 ? path.anchors[idx - 1] : null);
  const next = closed ? path.anchors[(idx + 1) % n] : (idx < n - 1 ? path.anchors[idx + 1] : null);
  if (!prev || !next) return;
  // Tangent direction = vector from prev to next, normalized.
  const tz = next.z - prev.z;
  const tr = next.r - prev.r;
  const len = Math.hypot(tz, tr);
  if (len < 1e-6) return;
  const ux = tz / len, uy = tr / len;
  // Handle length: fraction of distance to each neighbor.
  const lenIn = Math.hypot(a.z - prev.z, a.r - prev.r) * strength;
  const lenOut = Math.hypot(next.z - a.z, next.r - a.r) * strength;
  // Convert anchor to cubic and place handles along the tangent.
  a.kind = 'cubic';
  a.continuity = 'smooth';
  a.cp1 = { z: prev.z + (a.z - prev.z) * (1 - strength), r: prev.r + (a.r - prev.r) * (1 - strength) };
  a.cp2 = { z: a.z - ux * lenOut, r: a.r - uy * lenOut };
  void lenIn;
}

/** Smooth ALL interior anchors of a path by setting them to cubic with
 *  Catmull-Rom-style tangent handles. Endpoints stay as lines. */
export function smoothPath(path: PenPath, strength = 0.25): void {
  const n = path.anchors.length;
  if (n < 3) return;
  const start = path.closed ? 0 : 1;
  const end = path.closed ? n : n - 1;
  for (let i = start; i < end; i++) smoothAnchor(path, i, strength);
}

/** Z-monotonicity check — for vase profile (open path). True when the path's
 *  z values strictly ascend. Slicer requires this; otherwise STL will be inverted. */
export function isProfileZMonotonic(path: PenPath, samples = 128): boolean {
  if (path.closed) return false;
  const poly = tessellatePath(path, samples);
  for (let i = 1; i < poly.length; i++) {
    if (poly[i].z <= poly[i - 1].z - 1e-3) return false;
  }
  return true;
}

/** For a CLOSED pen path interpreted as a 2D cross-section in (x=z, y=r) coords,
 *  produce a polar LUT of length `samples`: lut[i] = ratio at angle θ_i where
 *  θ_i = (i / samples) * 2π. The ratio is the radial distance from the path
 *  centroid to the boundary, divided by the mean radial distance. So a perfect
 *  circle → all 1.0 (no shape change), a square → ~0.78..1.11 sweep, etc.
 *  When `path` is missing/invalid/open, returns null. */
export function crossSectionLUT(path: PenPath | null | undefined, samples = 64): Float32Array | null {
  if (!path || !path.closed || path.anchors.length < 3) return null;
  const poly = tessellatePath(path, 128);
  if (poly.length < 3) return null;
  let cx = 0, cy = 0;
  for (const p of poly) { cx += p.z; cy += p.r; }
  cx /= poly.length; cy /= poly.length;
  const out = new Float32Array(samples);
  let sum = 0;
  for (let i = 0; i < samples; i++) {
    const theta = (i / samples) * Math.PI * 2;
    const dx = Math.cos(theta), dy = Math.sin(theta);
    let bestT = 0;
    for (let j = 0; j < poly.length; j++) {
      const a = poly[j];
      const b = poly[(j + 1) % poly.length];
      const sx = b.z - a.z, sy = b.r - a.r;
      const det = sx * (-dy) - (-dx) * sy;
      if (Math.abs(det) < 1e-9) continue;
      const ex = cx - a.z, ey = cy - a.r;
      const s = (ex * (-dy) - (-dx) * ey) / det;
      const t = (sx * ey - sy * ex) / det;
      if (s >= 0 && s <= 1 && t > bestT) bestT = t;
    }
    out[i] = bestT;
    sum += bestT;
  }
  const mean = sum / samples;
  if (mean < 1e-6) return null;
  for (let i = 0; i < samples; i++) out[i] = out[i] / mean;
  return out;
}

/** Sample a cross-section LUT at angle θ (radians). Linear interpolation between
 *  neighboring samples. Returns 1.0 (no scaling) when LUT is null. */
export function sampleCrossSection(lut: Float32Array | null, theta: number): number {
  if (!lut) return 1;
  const n = lut.length;
  const t = ((theta / (Math.PI * 2)) % 1 + 1) % 1;
  const fi = t * n;
  const i0 = Math.floor(fi) % n;
  const i1 = (i0 + 1) % n;
  const f = fi - Math.floor(fi);
  return lut[i0] * (1 - f) + lut[i1] * f;
}

